/**
 * Shuttle Payment Manager
 * Handles payment method selection and redirect to payment processor
 * 
 * @package A2A_Theme
 */

class ShuttlePaymentManager extends ShuttleBookingManager {
    constructor() {
        super(true); // Skip auto-init from parent
        
        this.orderData = null;
        this.selectedMethod = 'card'; // Default
        this.translatableStrings = this.loadTranslatableStrings();
    }
    
    /**
     * Load translatable strings from hidden DOM element
     */
    loadTranslatableStrings() {
        const container = document.getElementById('shuttle-payment-translatable-strings');
        if (!container) {
            console.warn('Payment translatable strings container not found');
            return {};
        }
        
        const strings = {};
        const spans = container.querySelectorAll('span[data-key]');
        spans.forEach(span => {
            const key = span.getAttribute('data-key');
            const text = span.textContent;
            strings[key] = text;
        });
        
        return strings;
    }
    
    /**
     * Get translated string with placeholder replacement
     * @param {string} key - Translation key
     * @param {Array} replacements - Values to replace {0}, {1}, etc.
     * @returns {string} Translated string with replacements
     */
    getTranslatedString(key, replacements = []) {
        const template = this.translatableStrings[key];
        if (!template) {
            console.warn(`Translation key not found: ${key}`);
            return '';
        }
        
        let result = template;
        replacements.forEach((value, index) => {
            result = result.replace(`{${index}}`, value);
        });
        
        return result;
    }
    
    /**
     * Initialize payment manager
     */
    init() {
        // Call parent init
        super.init();
        
        // Check for order data in URL parameters or localStorage
        this.orderData = this.getOrderData();
        
        if (!this.orderData || !this.orderData.key) {
            console.error('[ShuttlePayment] No order data found, redirecting to landing page');
            this.redirectToLandingPage();
            return;
        }
        
        // Update prices on page
        this.updatePaymentPrices();
        
        // Update booking summary
        this.updateBookingSummary();
        
        // Bind payment method selection
        this.bindPaymentMethodSelection();
        
        // Bind pay button
        this.bindPayButton();
        
        // Set up currency change listener
        this.setupCurrencyListener();
        
        // Track GA UA Enhanced Ecommerce: Checkout Step 2 (STEP 4)
        // Fires when payment page loads
        this.trackCheckoutStep2UA();
    }
    
    /**
     * Set up currency change listener
     * Listens for currency changes from the header currency switcher
     */
    setupCurrencyListener() {
        window.addEventListener('shuttleCurrencyChanged', async (event) => {
            await this.handleCurrencyChange(event.detail);
        });
    }
    
    /**
     * Override parent method to refresh payment page with new currency
     */
    async refreshCurrentView() {
        // Update payment prices with new currency
        this.updatePaymentPrices();
        
        // Update summary with new currency
        this.updateBookingSummary();
    }
    
    /**
     * Get order data from localStorage (stored after booking submission)
     */
    getOrderData() {
        try {
            const orderDataStr = localStorage.getItem('shuttle_order_data');
            if (orderDataStr) {
                return JSON.parse(orderDataStr);
            }
        } catch (e) {
            console.error('[ShuttlePayment] Error parsing order data:', e);
        }
        return null;
    }
    
    /**
     * Update payment prices based on currency and order data
     */
    updatePaymentPrices() {
        const currency = this.currencyManager.currentCurrency;
        
        // Get total from booking data
        const total = this.calculateTotalPrice();
        
        // For PayPal, use the returned price if available (includes fee)
        let paypalTotal = total;
        if (this.orderData.paypal) {
            const paypalKey = `total_${currency.toLowerCase()}`;
            if (this.orderData.paypal[paypalKey]) {
                paypalTotal = this.orderData.paypal[paypalKey];
            }
        }
        
        // Update card price (Visa/Mastercard) - formatPrice already includes currency symbol
        const cardPrice = this.currencyManager.formatPrice(total, currency);
        const cardPriceText = this.getTranslatedString('pay', [cardPrice]) || `Pay ${cardPrice}`;
        jQuery('#payment-card-price').text(cardPriceText);
        
        // Update Amex price (same as card)
        jQuery('#payment-amex-price').text(cardPriceText);
        
        // Update PayPal price (with fee) - formatPrice already includes currency symbol
        const paypalPrice = this.currencyManager.formatPrice(paypalTotal, currency);
        const paypalPriceText = this.getTranslatedString('payIncludingFee', [paypalPrice]) || `Pay ${paypalPrice} including transaction fee`;
        jQuery('#payment-paypal-price').text(paypalPriceText);
        
        // Update pay button
        this.updatePayButton();
    }
    
    /**
     * Calculate total price from booking data in current currency
     * Gets the actual calculated total that was used in the booking and converts to current currency
     */
    calculateTotalPrice() {
        let totalInEUR = 0;
        const currentCurrency = this.currencyManager.currentCurrency;
        
        // Get totals from stored order data (includes all extras and fare upgrades)
        if (this.orderData && this.orderData.calculatedTotals) {
            // Sum outbound total (in EUR)
            if (this.orderData.calculatedTotals.outbound) {
                totalInEUR += this.orderData.calculatedTotals.outbound.price_eur || 0;
            }
            
            // Sum return total if exists (in EUR)
            if (this.orderData.calculatedTotals.return) {
                totalInEUR += this.orderData.calculatedTotals.return.price_eur || 0;
            }
            
            // Convert to current currency if not EUR
            if (currentCurrency === 'EUR') {
                return totalInEUR;
            } else {
                const convertedTotal = this.currencyManager.convertPrice(totalInEUR, 'EUR', currentCurrency);
                return convertedTotal;
            }
        }
        
        // Fallback: Try to get from order API response if it provides an amount field
        if (this.orderData && this.orderData.amount) {
            // Assume this is in EUR and convert
            if (currentCurrency === 'EUR') {
                return this.orderData.amount;
            } else {
                return this.currencyManager.convertPrice(this.orderData.amount, 'EUR', currentCurrency);
            }
        }
        
        // Last resort: Calculate from booking data (base seat price + real tier delta,
        // minus the admin-configured return discount; extras are not tracked in this fallback path)
        const fareTier = this.bookingData.pricePreferences?.tier || 'basic';
        const totalPax = (this.bookingData.passengers?.adults || 0) + (this.bookingData.passengers?.children || 0);
        
        if (this.bookingData.selectedRoutes?.outbound) {
            const route = this.bookingData.selectedRoutes.outbound;
            const price = route.price?.amount || 0;
            totalInEUR += (price + this.getTierDeltaEUR(route, fareTier)) * totalPax;
        }
        
        if (this.bookingData.selectedRoutes?.return) {
            const route = this.bookingData.selectedRoutes.return;
            const price = route.price?.amount || 0;
            const legTotal = (price + this.getTierDeltaEUR(route, fareTier)) * totalPax;
            totalInEUR += legTotal - (legTotal * this.getReturnDiscountRate());
        }
        
        // Convert to current currency
        if (currentCurrency === 'EUR') {
            return totalInEUR;
        } else {
            return this.currencyManager.convertPrice(totalInEUR, 'EUR', currentCurrency);
        }
    }
    
    /**
     * Update booking summary with current data
     */
    updateBookingSummary() {
        // Update route and date information
        this.updateSummaryRouteInfo();
        
        // Update line items (seats, extras, fares)
        this.updateSummaryLineItems();
        
        // Update total
        this.updateSummaryTotal();
    }
    
    /**
     * Update summary route and date information
     */
    updateSummaryRouteInfo() {
        const isReturnTrip = this.bookingData.transferType === 'return';
        
        // Update outbound route
        if (this.bookingData.from && this.bookingData.to) {
            const outboundRoute = `${this.bookingData.from.label || 'Unknown'} → ${this.bookingData.to.label || 'Unknown'}`;
            jQuery('[data-route-text="outbound"]').text(outboundRoute);
        }
        
        // Update outbound date
        if (this.bookingData.selectedRoutes?.outbound?.routeData?.date) {
            const outboundDate = this.formatSummaryDate(this.bookingData.selectedRoutes.outbound.routeData.date);
            jQuery('[data-date-text="outbound"]').text(outboundDate);
        }
        
        // Show/hide return section
        if (isReturnTrip) {
            jQuery('#summary-return-section').show();
            
            // Update return route (reversed)
            if (this.bookingData.from && this.bookingData.to) {
                const returnRoute = `${this.bookingData.to.label || 'Unknown'} → ${this.bookingData.from.label || 'Unknown'}`;
                jQuery('[data-route-text="return"]').text(returnRoute);
            }
            
            // Update return date
            if (this.bookingData.selectedRoutes?.return?.routeData?.date) {
                const returnDate = this.formatSummaryDate(this.bookingData.selectedRoutes.return.routeData.date);
                jQuery('[data-date-text="return"]').text(returnDate);
            }
            
            // Update trip title
            jQuery('[data-trip-title="outbound"]').text('Outbound');
        } else {
            jQuery('#summary-return-section').hide();
            jQuery('[data-trip-title="outbound"]').text('One-Way Transfer');
        }
    }
    
    /**
     * Format date for summary display
     */
    formatSummaryDate(dateString) {
        // Date format: "30.09.25, 08:30" → "Monday, 30 Sep 2025, 08:30"
        const parts = dateString.split(', ');
        if (parts.length !== 2) return dateString;
        
        const [datePart, timePart] = parts;
        const [day, month, year] = datePart.split('.');
        
        const fullYear = '20' + year;
        const date = new Date(fullYear, month - 1, day);
        
        const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        
        return `${weekday}, ${day} ${monthName} ${fullYear}, ${timePart}`;
    }
    
    /**
     * Update summary line items
     */
    updateSummaryLineItems() {
        // Update outbound line items
        this.updateTripLineItems('outbound');
        
        // Update return line items if applicable
        if (this.bookingData.transferType === 'return') {
            this.updateTripLineItems('return');
        }
    }
    
    /**
     * Update line items for a specific trip
     */
    updateTripLineItems(direction) {
        const container = jQuery(`[data-line-items="${direction}"]`);
        if (!container.length) return;
        
        container.empty();
        
        const route = this.bookingData.selectedRoutes[direction];
        if (!route) return;
        
        const currency = this.currencyManager.currentCurrency;
        const passengers = (this.bookingData.passengers?.adults || 0) + (this.bookingData.passengers?.children || 0);
        
        // Get calculated totals if available
        let lineItems = [];
        
        // Helper function to convert EUR prices to current currency
        const convertPrice = (eurPrice) => {
            if (currency === 'EUR') {
                return eurPrice;
            }
            return this.currencyManager.convertPrice(eurPrice, 'EUR', currency);
        };
        
        // Seats (price is in EUR)
        const seatPriceEUR = route.price?.amount || 0;
        const seatPriceConverted = convertPrice(seatPriceEUR);
        lineItems.push({
            name: 'Seats',
            quantity: `x${passengers}`,
            price: this.currencyManager.formatPrice(seatPriceConverted * passengers, currency)
        });
        
        // Fare condition (if not basic) - real per-route tier delta in EUR
        const fareCondition = this.bookingData.pricePreferences?.tier || 'skier';
        if (fareCondition !== 'basic') {
            const fareName = fareCondition === 'skier' ? 'Alps2Alps Skier' : 'Alps2Alps Pro';
            const fareUpgradeEUR = this.getTierDeltaEUR(route, fareCondition);
            const fareUpgradeConverted = convertPrice(fareUpgradeEUR);
            lineItems.push({
                name: fareName,
                quantity: `x${passengers}`,
                price: this.currencyManager.formatPrice(fareUpgradeConverted * passengers, currency)
            });
        }
        
        // Return discount (admin-configured % off transfer + tier price, return leg only)
        if (direction === 'return') {
            const discountRate = this.getReturnDiscountRate();
            const perPaxEUR = seatPriceEUR + this.getTierDeltaEUR(route, fareCondition);
            const discountEUR = perPaxEUR * passengers * discountRate;
            if (discountEUR > 0) {
                const discountConverted = convertPrice(discountEUR);
                const discountPercentLabel = Number((discountRate * 100).toFixed(1));
                lineItems.push({
                    name: `Return discount (${discountPercentLabel}% off)`,
                    quantity: '',
                    price: `-${this.currencyManager.formatPrice(discountConverted, currency)}`
                });
            }
        }
        
        // Extras (prices are in EUR)
        if (this.bookingData.luggage) {
            if (this.bookingData.luggage.additional_bag > 0) {
                const luggagePriceConverted = convertPrice(20);
                lineItems.push({
                    name: 'Additional luggage',
                    quantity: `x${this.bookingData.luggage.additional_bag}`,
                    price: this.currencyManager.formatPrice(luggagePriceConverted * this.bookingData.luggage.additional_bag, currency)
                });
            }
            if (this.bookingData.luggage.ski_bag > 0) {
                const skiPriceConverted = convertPrice(20);
                lineItems.push({
                    name: 'Additional ski / snowboard bags',
                    quantity: `x${this.bookingData.luggage.ski_bag}`,
                    price: this.currencyManager.formatPrice(skiPriceConverted * this.bookingData.luggage.ski_bag, currency)
                });
            }
        }
        
        if (this.bookingData.childSeats) {
            if (this.bookingData.childSeats.regular > 0) {
                const childSeatPriceConverted = convertPrice(2);
                lineItems.push({
                    name: 'Child seat',
                    quantity: `x${this.bookingData.childSeats.regular}`,
                    price: this.currencyManager.formatPrice(childSeatPriceConverted * this.bookingData.childSeats.regular, currency)
                });
            }
            if (this.bookingData.childSeats.booster > 0) {
                const boosterPriceConverted = convertPrice(2);
                lineItems.push({
                    name: 'Child booster seat',
                    quantity: `x${this.bookingData.childSeats.booster}`,
                    price: this.currencyManager.formatPrice(boosterPriceConverted * this.bookingData.childSeats.booster, currency)
                });
            }
        }
        
        // Render line items
        lineItems.forEach(item => {
            const lineItem = jQuery(`
                <div class="shuttle-checkout-summary__line-item">
                    <div class="shuttle-checkout-summary__item-info">
                        <p class="shuttle-checkout-summary__item-name">${item.name}</p>
                    </div>
                    <div class="shuttle-checkout-summary__item-pricing">
                        <span class="shuttle-checkout-summary__item-quantity">${item.quantity}</span>
                        <span class="shuttle-checkout-summary__item-price">${item.price}</span>
                    </div>
                </div>
            `);
            container.append(lineItem);
        });
    }
    
    /**
     * Update summary total
     * Includes PayPal fee if PayPal is selected
     */
    updateSummaryTotal() {
        const method = this.selectedMethod;
        const currency = this.currencyManager.currentCurrency;
        
        let total = this.calculateTotalPrice();
        
        // Use PayPal price if selected (includes transaction fee)
        if (method === 'paypal' && this.orderData.paypal) {
            const paypalKey = `total_${currency.toLowerCase()}`;
            if (this.orderData.paypal[paypalKey]) {
                total = this.orderData.paypal[paypalKey];
            }
        }
        
        const formattedTotal = this.currencyManager.formatPrice(total, currency);
        jQuery('#payment-total-amount').text(formattedTotal);
    }
    
    /**
     * Bind payment method radio selection
     */
    bindPaymentMethodSelection() {
        const self = this;
        
        jQuery('input[name="payment_method"]').on('change', function() {
            const method = jQuery(this).val();
            self.selectedMethod = method;
            
            // Update selected state styling
            jQuery('.shuttle-payment-option').removeClass('shuttle-payment-option--selected');
            jQuery(this).closest('.shuttle-payment-option').addClass('shuttle-payment-option--selected');
            
            // Update pay button
            self.updatePayButton();
            
            // Update summary total (to reflect PayPal fee if selected)
            self.updateSummaryTotal();
        });
        
        // Set initial selected state
        jQuery('.shuttle-payment-option[data-method="card"]').addClass('shuttle-payment-option--selected');
    }
    
    /**
     * Update pay button text with selected method price
     */
    updatePayButton() {
        const method = this.selectedMethod;
        const currency = this.currencyManager.currentCurrency;
        
        let total = this.calculateTotalPrice();
        
        // Use PayPal price if selected
        if (method === 'paypal' && this.orderData.paypal) {
            const paypalKey = `total_${currency.toLowerCase()}`;
            if (this.orderData.paypal[paypalKey]) {
                total = this.orderData.paypal[paypalKey];
            }
        }
        
        // formatPrice already includes currency symbol
        const formattedPrice = this.currencyManager.formatPrice(total, currency);
        const buttonText = this.getTranslatedString('payNow', [formattedPrice]) || `Pay Now ${formattedPrice}`;
        
        // Update both desktop and mobile buttons
        jQuery('#pay-now-btn').text(buttonText);
        jQuery('#pay-now-btn-mobile').text(buttonText);
    }
    
    /**
     * Bind pay button click
     * Handles both desktop (in payment options) and mobile (below summary) buttons
     */
    bindPayButton() {
        const self = this;
        
        // Desktop button
        jQuery('#pay-now-btn').on('click', function() {
            self.proceedToPayment();
        });
        
        // Mobile button
        jQuery('#pay-now-btn-mobile').on('click', function() {
            self.proceedToPayment();
        });
    }
    
    /**
     * Proceed to payment processor
     */
    proceedToPayment() {
        if (!this.orderData || !this.orderData.key) {
            console.error('[ShuttlePayment] No order key found');
            this.showError('Order key not found. Please try again.');
            return;
        }
        
        const apiBaseUrl = a2a_get_api_base_url();
        const orderKey = this.orderData.key;
        const method = this.selectedMethod;
        
        // Fire GA event for pay clicked with payment method (existing event)
        this.trackPayClicked(method);
        
        // Build language-aware return URLs for payment processor
        // The backend will redirect to these URLs after payment success/failure
        const thankYouUrl = window.shuttleThankYouUrl || '/shuttle-thank-you/';
        const paymentFailedUrl = window.shuttlePaymentFailedUrl || '/shuttle-payment-failed/';
        
        const successUrl = this.buildLanguageAwareUrl(thankYouUrl);
        const failureUrl = this.buildLanguageAwareUrl(paymentFailedUrl);
        
        // Build payment URL with return URLs
        // The backend API will use these URLs to redirect back with the correct language prefix
        const paymentUrl = `${apiBaseUrl}/booking/shuttle/checkout?key=${orderKey}&method=${method}&success_url=${encodeURIComponent(successUrl)}&failure_url=${encodeURIComponent(failureUrl)}`;
        
        // Redirect to payment processor
        window.location.href = paymentUrl;
    }
    
    /**
     * Track pay clicked GA event with payment method (existing event)
     */
    trackPayClicked(method) {
        // Map internal method names to GA parameter values
        const methodMap = {
            'card': 'card',
            'amex': 'card', // Amex is still a card
            'paypal': 'paypal'
        };
        
        const paymentMethod = methodMap[method] || method;
        
        if (typeof window.gaEvents !== 'undefined' && typeof window.gaEvents.trackPayClickedWithMethod === 'function') {
            window.gaEvents.trackPayClickedWithMethod(paymentMethod);
        }
    }
    
    /**
     * Track GA UA Enhanced Ecommerce: Checkout Step 2 (STEP 4)
     * Fires when payment page loads
     */
    trackCheckoutStep2UA() {
        if (!window.ShuttleGAEvents || !this.orderData) {
            return;
        }
        
        // Get order ID from stored order data
        const orderId = this.orderData.id;
        if (!orderId) {
            return;
        }
        
        // Get route_id from selected routes if available
        const routeId = this.bookingData.selectedRoutes?.outbound?.routeId || 
                       this.bookingData.selectedRoutes?.outbound?.id || null;
        
        // Track the event with orderData for accurate EUR pricing with extras
        const gaEvents = new window.ShuttleGAEvents();
        gaEvents.trackCheckoutStep2(this.bookingData, orderId, routeId, this.orderData);
    }
    
    /**
     * Show error message
     */
    showError(message) {
        alert(message);
    }
    
    /**
     * Redirect to landing page
     */
    redirectToLandingPage() {
        if (window.shuttleLandingPageUrl) {
            window.location.href = this.buildLanguageAwareUrl(window.shuttleLandingPageUrl || '/shuttle-transfers/');
        }
    }
}

// Initialize payment manager when DOM is ready
(function() {
    if (typeof jQuery !== 'undefined') {
        jQuery(document).ready(function() {
            if (jQuery('#shuttle-payment-app').length > 0) {
                window.shuttlePaymentManager = new ShuttlePaymentManager();
                window.shuttlePaymentManager.init();
            }
        });
    } else {
        document.addEventListener('DOMContentLoaded', function() {
            if (document.getElementById('shuttle-payment-app')) {
                window.shuttlePaymentManager = new ShuttlePaymentManager();
                window.shuttlePaymentManager.init();
            }
        });
    }
})();

